/**
 * Der Heim-PC: ein Browserfenster mit drei Reitern.
 *
 * Einkauf zeigt den Katalog mit Gerätebildern und kauft in das Lager eines
 * Standorts. Konsole öffnet die Serverkonsole für einen Server. Lager zeigt,
 * was gekauft wurde und wo es gerade steht. Der Einbau passiert nicht hier,
 * sondern im Rechenzentrum – dorthin führt der Reiter Lager.
 */
import { useMemo, useState, type ComponentProps } from 'react';
import { Link } from 'react-router-dom';
import type { ServerRole } from '@hosting-sim/core';
import { Badge, Button, EmptyState, Field, Input, Modal, Select, Tabs } from '../ui';
import { useGame } from '../../store/game';
import { useCatalog } from '../../pages/Infrastructure';
import { KIND_LABEL, shelf, type ShelfItem } from '../../pages/Shop';
import { ServerConsole } from '../ServerConsole';
import { Terminal } from './Terminal';
import { DeviceArt } from '../DeviceArt';
import { money, num, watt } from '../../lib/format';

/** Auswahlfeld mit einer Optionsliste – übersetzt sie in die Kinder von `Select`. */
function Pick({ options, ...props }: Omit<ComponentProps<typeof Select>, 'children'> & { options: { value: string; label: string }[] }) {
  return (
    <Select {...props}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </Select>
  );
}

const ROLE_OPTIONS: { value: ServerRole; label: string }[] = [
  { value: 'shared-hosting', label: 'Shared Hosting' },
  { value: 'vps-host', label: 'VPS-Host' },
  { value: 'dedicated', label: 'Dedicated' },
  { value: 'storage', label: 'Storage' },
  { value: 'mail', label: 'Mail' },
  { value: 'dns', label: 'DNS' },
  { value: 'backup', label: 'Backup' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  gameId: string;
}

export function HomePc({ open, onClose, gameId }: Props) {
  const [tab, setTab] = useState('einkauf');

  return (
    <Modal open={open} onClose={onClose} title="Heim-PC" wide>
      <div className="overflow-hidden rounded-xl border border-slate-700 bg-slate-900 text-slate-100 shadow-2xl">
        {/* Adressleiste */}
        <div className="flex items-center gap-2 border-b border-slate-700 bg-slate-800 px-3 py-2">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-slate-600" />
            <span className="h-2.5 w-2.5 rounded-full bg-slate-600" />
            <span className="h-2.5 w-2.5 rounded-full bg-slate-600" />
          </div>
          <div className="ml-2 flex-1 truncate rounded-md bg-slate-900 px-3 py-1 font-mono text-xs text-slate-400">
            https://hostingsim.local/{tab}
          </div>
        </div>

        <div className="p-4">
          <Tabs
            tabs={[
              { id: 'einkauf', label: 'Einkauf' },
              { id: 'konsole', label: 'Konsole' },
              { id: 'lager', label: 'Lager' },
              { id: 'terminal', label: 'Terminal' },
            ]}
            active={tab}
            onChange={setTab}
          />
          <div className="mt-4">
            {tab === 'einkauf' && <Shopfront />}
            {tab === 'konsole' && <ConsoleTab />}
            {tab === 'lager' && <StorageTab gameId={gameId} onClose={onClose} />}
            {tab === 'terminal' && <Terminal />}
          </div>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Einkauf
// ---------------------------------------------------------------------------

function Shopfront() {
  const catalog = useCatalog();
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<'all' | ShelfItem['kind']>('all');
  const [chosen, setChosen] = useState<ShelfItem | null>(null);

  const items = useMemo(() => (catalog ? shelf(catalog) : []), [catalog]);
  const visible = items
    .filter((item) => kind === 'all' || item.kind === kind)
    .filter((item) => {
      const needle = search.trim().toLowerCase();
      return !needle || `${item.name} ${item.description} ${item.specs}`.toLowerCase().includes(needle);
    });

  if (!catalog) return <EmptyState title="Katalog lädt …" />;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[200px] flex-1">
          <Input label="Suche" placeholder="Modell, Leistung …" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="w-48">
          <Pick
            label="Art"
            value={kind}
            onChange={(e) => setKind(e.target.value as 'all' | ShelfItem['kind'])}
            options={[{ value: 'all', label: 'Alle' }, ...Object.entries(KIND_LABEL).map(([value, label]) => ({ value, label }))]}
          />
        </div>
      </div>

      <p className="text-xs text-slate-400">
        Gekauftes wird ins Lager eines Standorts geliefert. Eingebaut wird es vor Ort im Rechenzentrum.
      </p>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((item) => (
          <button
            key={`${item.kind}-${item.key}`}
            type="button"
            onClick={() => setChosen(item)}
            className="group flex flex-col rounded-md border border-slate-700 bg-slate-800/60 p-3 text-left transition hover:border-slate-500 hover:bg-slate-800"
          >
            <div className="flex h-28 items-center justify-center rounded-md bg-slate-950/60">
              <DeviceArt kind={item.kind} heightUnits={item.heightUnits} tier={item.vendorTier} className="h-full w-full p-2" />
            </div>
            <div className="mt-3 flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-semibold">{item.name}</div>
                <div className="text-xs text-slate-400">{KIND_LABEL[item.kind]} · {item.central ? 'Zuleitung' : `${item.heightUnits} HE`}</div>
              </div>
              <Badge tone="neutral">{money(item.price)}</Badge>
            </div>
            <div className="mt-2 line-clamp-2 text-xs text-slate-400">{item.specs}</div>
            <div className="mt-2 text-[11px] text-slate-500">{watt(item.maxWatt)} Höchstlast</div>
          </button>
        ))}
      </div>

      {chosen && <PurchaseDialog item={chosen} onClose={() => setChosen(null)} />}
    </div>
  );
}

function PurchaseDialog({ item, onClose }: { item: ShelfItem; onClose: () => void }) {
  const world = useGame((s) => s.world);
  const run = useGame((s) => s.run);
  const [datacenterId, setDatacenterId] = useState('');
  const [role, setRole] = useState<ServerRole>('shared-hosting');
  const [busy, setBusy] = useState(false);

  if (!world) return null;
  const datacenters = Object.values(world.datacenters);
  const dcId = datacenterId || datacenters[0]?.id || '';
  const racksHere = Object.values(world.racks).filter((r) => r.datacenterId === dcId);
  const rack = racksHere[0];
  const canBuy = item.central ? Boolean(dcId) : Boolean(rack);

  async function buy() {
    setBusy(true);
    try {
      const result = await run(
        item.central
          ? { type: 'hw/buyUps', datacenterId: dcId as never, modelKey: item.key }
          : item.kind === 'server'
            ? { type: 'hw/buyServer', rackId: rack!.id, modelKey: item.key, role }
            : item.kind === 'router'
              ? { type: 'hw/buyRouter', rackId: rack!.id, modelKey: item.key }
              : item.kind === 'switch'
                ? { type: 'hw/buySwitch', rackId: rack!.id, modelKey: item.key }
                : item.kind === 'firewall'
                  ? { type: 'hw/buyFirewall', rackId: rack!.id, modelKey: item.key }
                  : { type: 'hw/buyLoadBalancer', rackId: rack!.id, modelKey: item.key },
      );
      if (result.ok) onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`${item.name} bestellen`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button variant="primary" loading={busy} disabled={!canBuy} onClick={buy}>
            Für {money(item.price)} bestellen
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex justify-center rounded-lg bg-slate-950/60 p-4">
          <DeviceArt kind={item.kind} heightUnits={item.heightUnits} tier={item.vendorTier} className="h-36 w-full max-w-sm" />
        </div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <Field label="Art">{KIND_LABEL[item.kind]}</Field>
          <Field label="Preis">{money(item.price)}</Field>
          <Field label="Platz & Strom">{item.central ? `Zuleitung · ${watt(item.maxWatt)}` : `${item.heightUnits} HE · ${watt(item.maxWatt)}`}</Field>
        </div>
        <p className="text-sm text-slate-300">{item.description}</p>
        <p className="text-xs text-slate-400">{item.specs}</p>

        <Pick
          label="Standort für die Lieferung"
          value={dcId}
          onChange={(e) => setDatacenterId(e.target.value)}
          options={datacenters.map((dc) => ({ value: dc.id, label: dc.city }))}
        />
        {item.kind === 'server' && (
          <Pick
            label="Vorgesehene Rolle"
            value={role}
            onChange={(e) => setRole(e.target.value as ServerRole)}
            options={ROLE_OPTIONS}
          />
        )}
        {!item.central && !rack && (
          <p className="text-sm text-amber-400">An diesem Standort gibt es noch kein Rack. Zuerst im Rechenzentrum ein Rack mieten.</p>
        )}
        <p className="text-xs text-slate-400">
          Die Lieferung landet im Lager. Danach im Rechenzentrum einbauen – das dauert einige Stunden.
        </p>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Konsole
// ---------------------------------------------------------------------------

function ConsoleTab() {
  const world = useGame((s) => s.world);
  const servers = world ? Object.values(world.servers) : [];
  const [selected, setSelected] = useState('');
  if (servers.length === 0) {
    return <EmptyState title="Noch kein Server" description="Kaufe einen Server und baue ihn im Rechenzentrum ein. Danach erscheint er hier." />;
  }
  const current = servers.find((s) => s.id === selected) ?? servers[0];
  return (
    <div className="space-y-3">
      <Pick
        label="Server"
        value={current.id}
        onChange={(e) => setSelected(e.target.value)}
        options={servers.map((s) => ({ value: s.id, label: `${s.name} · ${s.state}` }))}
      />
      <ServerConsole server={current} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lager
// ---------------------------------------------------------------------------

function StorageTab({ gameId, onClose }: { gameId: string; onClose: () => void }) {
  const world = useGame((s) => s.world);
  if (!world) return null;
  const items = Object.values(world.deliveries);
  if (items.length === 0) {
    return (
      <EmptyState
        title="Das Lager ist leer"
        description="Gekauftes Gerät landet hier, bis es im Rechenzentrum eingebaut ist."
        action={
          <Link to={`/game/${gameId}/vor-ort`} onClick={onClose}>
            <Button variant="secondary" size="sm">Zum Rechenzentrum</Button>
          </Link>
        }
      />
    );
  }
  return (
    <div className="space-y-3">
      {items.map((d) => {
        const dc = world.datacenters[d.datacenterId];
        const progress = d.install ? Math.max(0, d.install.doneTick - world.meta.tick) : null;
        return (
          <div key={d.id} className="flex items-center gap-3 rounded-lg border border-slate-700 bg-slate-800/60 p-3">
            <div className="h-16 w-24 shrink-0 rounded-md bg-slate-950/60 p-1">
              <DeviceArt kind={d.kind === 'loadbalancer' ? 'loadbalancer' : d.kind} heightUnits={d.heightUnits} className="h-full w-full" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold">{d.label}</div>
              <div className="text-xs text-slate-400">
                {dc?.city ?? 'Unbekannter Standort'} · bezahlt {money(d.price)}
              </div>
            </div>
            {d.install ? (
              <Badge tone="neutral">Einbau · noch {progress} Std.</Badge>
            ) : (
              <Badge tone="neutral">im Lager</Badge>
            )}
          </div>
        );
      })}
      <Link to={`/game/${gameId}/vor-ort`} onClick={onClose} className="block text-center text-sm text-slate-300 underline-offset-4 hover:underline">
        Zum Rechenzentrum fahren, um einzubauen →
      </Link>
      <p className="text-center text-xs text-slate-500">{num(items.length)} Lieferung(en) im Lager</p>
    </div>
  );
}
